import { useMutation } from '@apollo/client';
import { ColumnsType } from 'antd/lib/table';
import ButtonWithSpinner from 'components/common/ButtonWithSpinner';
import Table from 'components/Table';
import { MobileTableType } from 'components/Table/types';
import deviceSizes from 'constants/deviceSizes';
import { ERRORS } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import useReactiveQueryTable from 'hooks/useReactiveQueryTable';
import useWindowSize from 'hooks/useWindowSize';
import React, { useCallback, useEffect, useState } from 'react';
import { TableContainerStyled } from 'routes/Users/components/UsersList/styled';
import { TableDataProps } from 'routes/Users/components/UsersList/types';
import { ASSIGN_TRANSACTION, REMOVE_ASSIGN_TRANSACTION } from 'services/graphql/mutations/transactions';
import { GET_TRANSACTION_ASSIGNERS } from 'services/graphql/queries/user.query';
import { GeneralFunction } from 'types/common';
import { ITransaction } from 'types/transaction';
import { CompanyRoleEnum, IUser, RoleEnum, UserStatusEnum } from 'types/user';
import { defaultNotification } from 'utils';

import ClientSearchBar from './ClientSearchBar';

const NULL_RENDER = '--';

const ClientAssignTable: React.FC<{ transaction?: ITransaction; callbackOnAssignSuccess?: GeneralFunction }> = ({
  transaction,
  callbackOnAssignSuccess,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const currentUserMainLocation = useCurrentLoggedInUser()?.mainLocation;
  const dataMapper = useCallback(
    (u: IUser): TableDataProps & { user: { id: string; isAssignedTransaction: boolean } } => ({
      ...u,
      key: u.id,
      user: { id: u.id, isAssignedTransaction: !!u.isAssignedTransaction },
      status: u.status,
      companyRole: u.companyRole || CompanyRoleEnum.REGULAR,
    }),
    [],
  );

  const [assignTransaction, { loading: assignLoading }] = useMutation(ASSIGN_TRANSACTION);

  const onAssignUserClick = useCallback(
    async (userId: string) => {
      try {
        await assignTransaction({
          variables: {
            transaction: transaction?.id,
            assigner: userId,
          },
        });
        defaultNotification({ success: 'Assign transaction successfully!' });
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        refetch?.();
        callbackOnAssignSuccess?.();
      } catch (err) {
        defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
      }
    },
    [transaction?.id],
  );

  const [removeAssignTransaction, { loading: removeAssignLoading }] = useMutation(REMOVE_ASSIGN_TRANSACTION);

  const onRemoveAssignUserClick = useCallback(async (userId: string) => {
    try {
      await removeAssignTransaction({
        variables: {
          transaction: transaction?.id,
          assigner: userId,
        },
      });
      defaultNotification({ success: 'Remove transaction assignment successfully!' });
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      refetch?.();
      callbackOnAssignSuccess?.();
    } catch (err) {
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    }
  }, []);

  const originalColumns: ColumnsType<TableDataProps> = [
    {
      title: 'First Name',
      dataIndex: 'firstName',
      sorter: true,
      render: (text: string) => <span className='gx-font-weight-medium'>{text || NULL_RENDER}</span>,
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      sorter: true,
      render: (text: string) => <span className='gx-font-weight-medium'>{text || NULL_RENDER}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      sorter: true,
      render: (text: string) => <span className='break-all'>{text || NULL_RENDER}</span>,
    },
    {
      title: '',
      dataIndex: 'user',
      render: ({ id, isAssignedTransaction }) => (
        <div className='justify-center md:justify-items-start md:w-auto w-full'>
          {isAssignedTransaction ? (
            <ButtonWithSpinner
              buttonType='text'
              width='100%'
              color='#ff3030'
              spinnerColor='#ff3030'
              isLoading={removeAssignLoading}
              onClick={() => {
                onRemoveAssignUserClick(id);
              }}>
              Remove
            </ButtonWithSpinner>
          ) : (
            <ButtonWithSpinner
              buttonType='text'
              width='100%'
              color='#038fde'
              spinnerColor='#038fde'
              isLoading={assignLoading}
              onClick={() => {
                onAssignUserClick(id);
              }}>
              Assign
            </ButtonWithSpinner>
          )}
        </div>
      ),
    },
  ];

  const { refetch, ...tableProps } = useReactiveQueryTable({
    graphQLQuery: GET_TRANSACTION_ASSIGNERS,
    columns: originalColumns,
    dataMapper,
    queryProps: {
      variables: {
        query: searchValue,
        status: UserStatusEnum.ACTIVE,
        role: RoleEnum.CLIENT,
        sortAssignedTransaction: transaction?.id || null,
        mainLocationIds: [currentUserMainLocation?.id],
      },
      fetchPolicy: 'cache-and-network',
    },
  });

  const { loading } = tableProps;

  const mobileThreshold = 575;
  const { isMobile } = useWindowSize(mobileThreshold);

  useEffect(() => {
    if (!!tableProps.dataSource && !loading) {
      refetch();
    }
  }, [searchValue]);

  const onSearch = (v: string) => {
    setSearchValue(v);
  };

  useEffect(() => {
    refetch?.();
  }, [searchValue]);

  if (!transaction) return null;
  return (
    <div>
      <div className='gx-mb-2'>
        <ClientSearchBar onSearch={onSearch} />
      </div>
      <TableContainerStyled
        style={{
          padding: isMobile ? 15 : 0,
        }}
        span={24}>
        <Table
          {...tableProps}
          mobileDesignType={MobileTableType.RESPONSIVE_TABLE}
          widthThreshold={deviceSizes.numbers.md}
          // onRow={(record) => ({
          //   onClick: () => {
          //     setActiveUserId(record.key);
          //     tableProps.changeSlideInState?.(true);
          //     saveCurrentScrollPos();
          //   },
          // })}
          className='gx-table-responsive table-users'
        />
      </TableContainerStyled>
    </div>
  );
};

export default ClientAssignTable;
